import { useEffect, useRef, useState } from "react";
import {
	GitBranch,
	Loader2,
	ExternalLink,
	RefreshCw,
	Copy,
	Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PermissionGate } from "@/components/PermissionGate";
import { useApiSWR } from "@/hooks/useApiSWR";
import { PrBadge } from "../../github/PrBadge";
import {
	getTaskGithub,
	createTaskBranch,
	unlinkTaskGithub,
	type TaskGithubLink,
} from "@/services/task-github.service";

export function GithubSection({
	projectId,
	taskId,
	canEdit,
}: {
	projectId: string;
	taskId: string;
	canEdit: boolean;
}) {
	const {
		data,
		error: loadError,
		mutate,
	} = useApiSWR<TaskGithubLink | null>(["tasks", taskId, "github"], () =>
		getTaskGithub(projectId, taskId),
	);
	// data can legitimately be null (no link), so isLoading from useApiSWR
	// would stay true forever; only undefined means not yet fetched.
	const link = data ?? null;
	const loading = data === undefined && !loadError;
	const [busy, setBusy] = useState(false);
	const [actionError, setActionError] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const error = actionError ?? loadError?.message ?? null;

	useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
		};
	}, []);

	async function handleCreateBranch() {
		setBusy(true);
		setActionError(null);
		try {
			const created = await createTaskBranch(projectId, taskId);
			await mutate(created, { revalidate: false });
		} catch (e) {
			setActionError(
				e instanceof Error
					? e.message
					: "Could not create branch. Check the project's GitHub repo.",
			);
		} finally {
			setBusy(false);
		}
	}

	async function handleUnlink() {
		setBusy(true);
		try {
			await unlinkTaskGithub(projectId, taskId);
			await mutate(null, { revalidate: false });
		} catch (e) {
			setActionError(
				e instanceof Error ? e.message : "Failed to unlink.",
			);
		} finally {
			setBusy(false);
		}
	}

	async function handleRefresh() {
		setBusy(true);
		setActionError(null);
		try {
			await mutate(() => getTaskGithub(projectId, taskId, true), {
				revalidate: false,
			});
		} catch (e) {
			setActionError(
				e instanceof Error ? e.message : "Failed to refresh.",
			);
		} finally {
			setBusy(false);
		}
	}

	function copyBranch() {
		if (!link) return;
		navigator.clipboard?.writeText(link.branch_name);
		setCopied(true);
		if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
		copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
	}

	return (
		<section>
			<div className="flex items-center justify-between mb-3">
				<div className="flex items-center gap-2">
					<GitBranch className="h-4 w-4 text-muted" />
					<h3 className="text-sm font-semibold text-foreground">GitHub</h3>
				</div>
				{link && (
					<Button
						variant="ghost"
						size="sm"
						onClick={handleRefresh}
						disabled={busy}
						title="Refresh PR status"
					>
						<RefreshCw
							className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`}
						/>
					</Button>
				)}
			</div>

			{loading ? (
				<div className="flex items-center gap-2 text-xs text-muted">
					<Loader2 className="h-3.5 w-3.5 animate-spin" />
					Loading...
				</div>
			) : link ? (
				<div className="rounded-lg border border-border bg-muted-subtle/40 p-3 flex flex-col gap-2.5">
					<div className="flex items-center justify-between gap-2">
						<button
							type="button"
							onClick={copyBranch}
							className="inline-flex items-center gap-1.5 text-xs font-mono text-foreground hover:text-primary"
							title="Copy branch name"
						>
							<GitBranch className="h-3.5 w-3.5" />
							{link.branch_name}
							{copied ? (
								<Check className="h-3 w-3 text-secondary" />
							) : (
								<Copy className="h-3 w-3 opacity-60" />
							)}
						</button>
						<PrBadge state={link.pr_state} prNumber={link.pr_number} />
					</div>
					<div className="flex items-center justify-between">
						{link.pr_url ? (
							<a
								href={link.pr_url}
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
							>
								View pull request
								<ExternalLink className="h-3 w-3" />
							</a>
						) : (
							<span className="text-xs text-muted italic">
								No pull request yet. Open one from this branch.
							</span>
						)}
						{canEdit && (
							<Button
								variant="ghost"
								size="sm"
								onClick={handleUnlink}
								disabled={busy}
								className="text-xs text-muted"
							>
								Unlink
							</Button>
						)}
					</div>
				</div>
			) : (
				<div className="flex flex-col gap-2">
					<p className="text-xs text-muted">
						Create a GitHub branch for this task and track its PR status here.
					</p>
					<PermissionGate feature="Create & edit tasks">
						<Button
							size="sm"
							onClick={handleCreateBranch}
							disabled={busy || !canEdit}
							className="self-start"
						>
							{busy ? (
								<Loader2 className="h-4 w-4 animate-spin mr-2" />
							) : (
								<GitBranch className="h-4 w-4 mr-2" />
							)}
							Create branch
						</Button>
					</PermissionGate>
				</div>
			)}

			{error && <p className="text-xs text-danger mt-2">{error}</p>}
		</section>
	);
}
